/**
 * ICONIC: einfache Rollen und Sonderrechte für die Seiten «Benutzer».
 * Intern nutzt TeamSpeak Server-Gruppen, Kanal-Gruppen und Einzelrechte; die Oberfläche zeigt nur Rollen.
 * Getestet am 03.11.2026 gegen TeamSpeak 3.13.7 (docs/00-plan.md, Abschnitt «Benutzer & Rechte»).
 */
import socket from "../socket";
import TeamSpeak from "../api/TeamSpeak";
import store from "../store";

/** Stärke für Sonderrechte: reicht für Normale (brauchen 50) und Gäste (25), nie für Server Admins (75) */
export const SPECIAL_POWER = 50;

export const SERVER_ROLES = [
  {
    key: "admin",
    groupName: "Server Admin",
    label: "Server Admin",
    short: "darf fast alles auf dem Server",
    text:
      "Darf fast alles: Leute rauswerfen, verschieben und bannen, Rollen und Rechte ändern, Kanäle und Server-Einstellungen ändern. SuperAdmin kann er nicht rauswerfen oder verschieben.",
  },
  {
    key: "normal",
    groupName: "Normal",
    label: "Normal",
    short: "eigene Kanäle, keine Macht über andere",
    text:
      "Darf eigene Kanäle anlegen, Dateien hochladen, anstupsen und private Nachrichten schreiben. Hat keine Macht über andere.",
  },
  {
    key: "guest",
    groupName: null, // Standardgruppe des Servers
    label: "Gast",
    short: "darf rein, ohne besondere Rechte",
    text:
      "Darf auf den Server, Kanäle betreten und vorübergehende Kanäle anlegen. Wer neu kommt, ist automatisch Gast.",
  },
];

/** Kanal-Rollen in der Reihenfolge der Auswahl. «Kanal-Chef» legt die Oberfläche beim ersten Mal an. */
export const KANAL_CHEF = "Kanal-Chef";
export const CHANNEL_ROLES = [
  {
    key: "chef",
    groupName: KANAL_CHEF,
    label: "Kanal-Chef",
    text:
      "Alles in diesem Kanal: Name, Thema, Passwort und Sprechrecht ändern, Leute aus dem Kanal werfen und Kanal-Verwalter, Helfer und Sprecher ernennen. Wirkt nur auf Normale und Gäste, Admins und SuperAdmin kann er nicht anfassen.",
  },
  {
    key: "verwalter",
    groupName: "Channel Admin",
    label: "Kanal-Verwalter",
    text:
      "Kanal ändern (Name, Thema, Passwort, Sprechrecht) und Helfer und Sprecher ernennen. Darf niemanden rauswerfen. Diese Rolle bekommt automatisch, wer einen Kanal anlegt.",
  },
  {
    key: "helfer",
    groupName: "Operator",
    label: "Helfer",
    text: "Darf in diesem Kanal sprechen und anderen das Sprechen erlauben.",
  },
  {
    key: "sprecher",
    groupName: "Voice",
    label: "Darf sprechen",
    text:
      "Darf in diesem Kanal auch dann sprechen, wenn der Kanal ein Sprechrecht verlangt.",
  },
];

/** Werte der Rolle «Kanal-Chef» zusätzlich zur Kopie der Standard-Kanalrolle (Channel Admin) */
export const KANAL_CHEF_PERMS = [
  ["i_client_kick_from_channel_power", SPECIAL_POWER], // aus dem eigenen Kanal werfen: Normale und Gäste
  ["i_client_kick_from_server_power", 0],
  ["i_client_move_power", 0],
  ["i_client_ban_power", 0],
  ["i_client_needed_kick_from_channel_power", 60], // andere Kanal-Chefs können ihn nicht werfen
  ["i_group_needed_member_add_power", 75], // nur Server Admins und diese Oberfläche vergeben Kanal-Chef
  ["i_group_needed_member_remove_power", 75],
];

/** Sonderrechte auf dem ganzen Server, als Einzelrechte am Benutzer (ohne Gruppe) */
export const SPECIAL_RIGHTS = [
  {
    perm: "i_client_kick_from_channel_power",
    label: "Leute aus Kanälen werfen",
    text: "in jedem Kanal",
  },
  {
    perm: "i_client_move_power",
    label: "Leute verschieben",
    text: "in einen anderen Kanal ziehen",
  },
  {
    perm: "i_client_kick_from_server_power",
    label: "Leute vom Server werfen",
    text: "sie können gleich wieder rein",
  },
];

let configCache = null;

/** Einstellungen vom Server der Oberfläche (geschützte Benutzer) */
export function loadConfig() {
  if (configCache) return Promise.resolve(configCache);
  return new Promise((resolve) => {
    socket.emit("iconic-config", (res) => {
      configCache = {
        protectedDbIds: (res && res.protectedDbIds) || [],
      };
      resolve(configCache);
    });
  });
}

/** Wählt den ersten laufenden virtuellen Server, falls noch keiner gewählt ist */
export async function ensureServer() {
  // Maßgeblich ist die Query-Verbindung selbst, nicht das Cookie (nach neuem Login ist noch kein Server gewählt)
  const me = asList(await TeamSpeak.execute("whoami"))[0];
  if (me && Number(me.virtualserverId) > 0) {
    if (!store.state.query.serverId)
      await store.dispatch("saveServerId", me.virtualserverId);
    return;
  }
  const servers = asList(await TeamSpeak.execute("serverlist"));
  const online = servers.find((s) => s.virtualserverStatus === "online");
  if (online) await TeamSpeak.selectServer(online.virtualserverId);
}

export const asList = (r) => (Array.isArray(r) ? r : r ? [r] : []);

/** Gruppen-IDs der Standardrollen anhand der Namen und der Server-Standards */
export function resolveGroups(serverInfo, serverGroups, channelGroups) {
  const regularSg = serverGroups.filter((g) => String(g.type) === "1");
  const regularCg = channelGroups.filter((g) => String(g.type) === "1");
  const defaultSg = String(serverInfo.virtualserverDefaultServerGroup);
  const defaultCg = String(serverInfo.virtualserverDefaultChannelGroup);
  const defaultAdminCg = String(serverInfo.virtualserverDefaultChannelAdminGroup);

  const serverRoles = SERVER_ROLES.map((role) => {
    const group = role.groupName
      ? regularSg.find((g) => g.name === role.groupName)
      : regularSg.find((g) => String(g.sgid) === defaultSg);
    return { ...role, sgid: group ? String(group.sgid) : null };
  }).filter((r) => r.sgid);

  const channelRoles = CHANNEL_ROLES.map((role) => {
    const group = regularCg.find((g) => g.name === role.groupName);
    return { ...role, cgid: group ? String(group.cgid) : null };
  }).filter((r) => r.cgid || r.key === "chef");

  return {
    serverRoles,
    channelRoles,
    defaultSg,
    defaultCg,
    defaultAdminCg,
    sgName: (sgid) => (regularSg.find((g) => String(g.sgid) === String(sgid)) || {}).name,
    cgName: (cgid) => (regularCg.find((g) => String(g.cgid) === String(cgid)) || {}).name,
  };
}

/** Bezeichnung einer Kanal-Gruppe in der Oberfläche */
export function channelRoleLabel(groups, cgid) {
  const role = groups.channelRoles.find((r) => r.cgid === String(cgid));
  return role ? role.label : groups.cgName(cgid) || `Kanal-Gruppe ${cgid}`;
}

/** Server-Rolle eines Benutzers aus seinen Server-Gruppen */
export function serverRoleOf(groups, sgids) {
  const ids = sgids.map(String);
  for (const role of groups.serverRoles) {
    if (role.key !== "guest" && ids.includes(role.sgid)) return role;
  }
  return groups.serverRoles.find((r) => r.key === "guest") || null;
}

/** Kanalnamen mit Pfad («Oben › Unten»), ohne Trennlinien (Spacer) */
export function channelPaths(channels) {
  const byId = {};
  channels.forEach((c) => (byId[String(c.cid)] = c));
  const path = (c, depth = 0) => {
    const parent = byId[String(c.pid)];
    return parent && depth < 10 ? `${path(parent, depth + 1)} › ${c.channelName}` : c.channelName;
  };
  return channels
    .filter((c) => !/^\[[^\]]*spacer[^\]]*\]/i.test(c.channelName))
    .map((c) => ({ cid: String(c.cid), name: c.channelName, path: path(c) }));
}

/** Legt die Rolle «Kanal-Chef» an, falls es sie noch nicht gibt, und setzt ihre Werte */
export async function ensureKanalChef(groups) {
  const list = asList(await TeamSpeak.execute("channelgrouplist"));
  let group = list.find((g) => String(g.type) === "1" && g.name === KANAL_CHEF);
  let cgid = group && String(group.cgid);
  if (!cgid) {
    const created = asList(
      await TeamSpeak.execute("channelgroupcopy", {
        scgid: groups.defaultAdminCg,
        tcgid: 0,
        name: KANAL_CHEF,
        type: 1,
      })
    );
    cgid = String(created[0].cgid);
  }
  const perms = asList(
    await TeamSpeak.execute("channelgrouppermlist", { cgid }, ["-permsid"])
  );
  const has = (permsid, value) =>
    perms.some((p) => p.permsid === permsid && Number(p.permvalue) === value);
  if (!KANAL_CHEF_PERMS.every(([permsid, value]) => has(permsid, value))) {
    for (const [permsid, permvalue] of KANAL_CHEF_PERMS) {
      await TeamSpeak.execute("channelgroupaddperm", {
        cgid,
        permsid,
        permvalue,
        permnegated: 0,
        permskip: 0,
      });
    }
  }
  return cgid;
}

/** Verständliche Fehlermeldung */
export function errorText(err) {
  const msg = (err && (err.message || err.msg)) || String(err);
  if (/insufficient client permissions/i.test(msg)) return "Dafür fehlen die Rechte in TeamSpeak.";
  if (/invalid clientID|invalid client ?id/i.test(msg)) return "Diesen Benutzer gibt es nicht (mehr).";
  if (/invalid channelID/i.test(msg)) return "Diesen Kanal gibt es nicht (mehr).";
  if (/duplicate entry/i.test(msg)) return "Das ist schon so eingetragen.";
  return msg;
}

export function formatDate(seconds) {
  const n = Number(seconds);
  if (!n) return "–";
  return new Date(n * 1000).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function relativeDate(seconds) {
  const n = Number(seconds);
  if (!n) return "nie";
  const diff = Date.now() / 1000 - n;
  if (diff < 3600) return "vor weniger als einer Stunde";
  if (diff < 86400) return `vor ${Math.round(diff / 3600)} Std.`;
  const days = Math.round(diff / 86400);
  if (days < 60) return days === 1 ? "gestern" : `vor ${days} Tagen`;
  return formatDate(n).slice(0, 10);
}
